import { cn } from '@rouvydev/web-components/utils'
import {
  type ChangeEvent,
  type FocusEvent,
  useCallback,
  useEffect,
  useState,
  type FormEvent,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useFetcher } from 'react-router'
import { z } from 'zod'
import { parseSearchParams } from 'zod-search-params'
import { type Route } from './+types/route'
import { ArticleHeader } from '~/components/article-header.tsx'
import { Articles } from '~/components/articles.tsx'
import { Cols } from '~/components/cols.tsx'
import { GradientsContainer } from '~/components/gradients-container.tsx'
import { Gradient } from '~/components/gradients.tsx'
import { Icon } from '~/components/icon.tsx'
import { type InputProps } from '~/components/input.tsx'
import { Spinner } from '~/components/spinner.tsx'
import { blogPostsSearch } from '~/domains/blog-posts/blog-posts.server.ts'
import { getFormatter } from '~/utils/date-formatter.server'
import { fallbackLng, supportedLngs } from '~/utils/i18n.ts'
import i18next from '~/utils/i18next.server.ts'
import { generateMeta, type MetaProps } from '~/utils/meta.ts'
import { useDebounce } from '~/utils/misc.ts'
import { getPageState } from '~/utils/page-state.ts'

const minSearchLength = 3

export function meta({ data }: Route.MetaArgs) {
  return data?.metaProps
    ? generateMeta(data.metaProps as MetaProps)
    : [{ title: '404 not found' }]
}

export let handle = {
  i18n: 'blog',
}

export async function loader({ request }: Route.LoaderArgs) {
  const { locale, publicationState } = await getPageState(request)

  const { q } = parseSearchParams(
    z.object({
      q: z.string().optional().catch(''),
    }),
    new URL(request.url).searchParams,
  )

  const t = await i18next.getFixedT(locale ?? fallbackLng, 'blog')

  const formatter = await getFormatter(request)

  const blogPosts =
    q && q.length >= minSearchLength
      ? await blogPostsSearch({
          searchTerm: q ?? '',
          formatter,
          publicationState,
          locale,
        })
      : []

  const metaProps: MetaProps = {
    seo: {
      metaTitle: t('blog_meta_title'),
      metaDescription: t('blog_meta_description'),
    },
    localizations: supportedLngs,
    pageState: await getPageState(request),
  }

  return {
    metaProps,
    blogPosts,
  }
}

const SearchBlog = (props: InputProps) => {
  const { t } = useTranslation(['blog'])
  const fetcher = useFetcher<typeof loader>({ key: 'search-fetcher' })
  const submit = useDebounce(fetcher.submit, 400)

  const handleChange = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      const input = event.currentTarget.elements.namedItem(
        'q',
      ) as HTMLInputElement
      if (input.value.length >= minSearchLength || input.value.length === 0) {
        submit(event.currentTarget)
      }
    },
    [submit],
  )

  return (
    <div className="relative mt-6">
      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-6">
        <Icon name="search" className="size-8 text-icon-muted" />
      </div>
      <fetcher.Form id="search-form" role="search" onChange={handleChange}>
        <input
          id="q"
          name="q"
          placeholder={t('search_blogposts_placeholder')}
          defaultValue={''}
          className="block w-full bg-input-background-default py-6 pr-6 pl-16 text-body-20 text-global-white ring-1 ring-border-secondary outline-0 placeholder:text-input-text-disabled focus:ring-2 focus:ring-border-focus focus:outline-hidden"
          type="search"
          {...props}
        />
      </fetcher.Form>
    </div>
  )
}

export default function BlogSearch({
  loaderData: { blogPosts },
}: Route.ComponentProps) {
  const { t } = useTranslation(['blog'])

  const searchFetcher = useFetcher<typeof loader>({ key: 'search-fetcher' })

  const [searchPosts, setSearchPosts] = useState(blogPosts ?? [])
  const [searchLoading, setSearchLoading] = useState(false)
  const [searching, setSearching] = useState(false)

  useEffect(() => {
    if (searchFetcher.state === 'loading' || searchFetcher.data) {
      setSearchLoading(searchFetcher.state === 'loading')
    }

    if (searchFetcher.data) {
      setSearchPosts(searchFetcher.data.blogPosts as typeof blogPosts)
    }
  }, [searchFetcher])

  function handleStopSearching(
    event: ChangeEvent<HTMLInputElement> | FocusEvent<HTMLInputElement>,
  ) {
    if (!event.currentTarget.value || event.currentTarget.value === '') {
      setSearchPosts([])
      setSearching(false)
      setSearchLoading(false)
    }
  }

  return (
    <>
      <Gradient
        className={cn(
          searching ? 'opacity-100' : 'opacity-0',
          'transition-opacity duration-500',
        )}
      />
      <ArticleHeader title={t('blog')} showBg={!searching}>
        <SearchBlog
          onFocus={() => setSearching(true)}
          onInput={() => setSearching(true)}
          onBlur={handleStopSearching}
          onChange={handleStopSearching}
        />
      </ArticleHeader>

      <GradientsContainer>
        <div className="container">
          <Cols size="10">
            <div className="min-h-screen">
              {searchLoading && (
                <div className="flex w-full justify-center pt-20 md:pt-40">
                  <Spinner size="medium" />
                </div>
              )}
              {!searchLoading && searchPosts.length > 0 && (
                <div className="pt-12 pb-16">
                  <Articles isMainArticle={false} articles={searchPosts} />
                </div>
              )}
            </div>
          </Cols>
        </div>
      </GradientsContainer>
    </>
  )
}
